import { useRef } from 'react';
import { Doughnut } from 'react-chartjs-2';

import { baseDonutOptions } from './chartOptions.js';
import './registerCharts.js';
import { useChartPalette } from './useChartPalette.js';

/**
 * @param {{
 *   labels: string[],
 *   values: number[],
 *   colors?: string[],
 *   height?: number,
 *   legend?: boolean,            // always-visible value legend beside the ring
 *   formatValue?: (n: number) => string
 * }} props
 */
export function DonutChart({ labels, values, colors, height = 280, legend = false, formatValue }) {
  const palette = useChartPalette();
  const fallback = palette?.series || [];
  const colorFor = (i) => (colors && colors[i]) || fallback[i % (fallback.length || 1)];

  const wrapRef = useRef(null);
  const tipRef = useRef(null);

  const baseOptions = baseDonutOptions(palette || {});
  const options = {
    ...baseOptions,
    plugins: {
      ...baseOptions.plugins,
      ...(legend ? { legend: { display: false } } : {}),
      // Custom HTML tooltip that follows the cursor freely (not clamped to the canvas).
      tooltip: {
        ...(baseOptions.plugins?.tooltip || {}),
        enabled: false,
        external: (ctx) => {
          const el = tipRef.current;
          if (!el) return;
          const tt = ctx.tooltip;
          if (!tt || tt.opacity === 0) {
            el.classList.remove('is-visible');
            return;
          }
          const dp = tt.dataPoints && tt.dataPoints[0];
          if (dp) {
            const series = dp.dataset.data || [];
            const total = series.reduce((a, b) => a + Number(b || 0), 0);
            const val = Number(dp.parsed || 0);
            const pct = total ? Math.round((val / total) * 100) : 0;
            const shown = formatValue ? formatValue(val) : String(val);
            el.replaceChildren();
            const labelEl = document.createElement('span');
            labelEl.className = 'donut-tip-label';
            labelEl.textContent = dp.label;
            const valueEl = document.createElement('span');
            valueEl.className = 'donut-tip-value';
            valueEl.textContent = `${shown} · ${pct}%`;
            el.append(labelEl, valueEl);
          }
          el.classList.add('is-visible');
        },
      },
    },
  };

  const handleMove = (e) => {
    const wrap = wrapRef.current;
    const tip = tipRef.current;
    if (!wrap || !tip) return;
    const rect = wrap.getBoundingClientRect();
    tip.style.left = `${e.clientX - rect.left}px`;
    tip.style.top = `${e.clientY - rect.top}px`;
  };
  const handleLeave = () => tipRef.current?.classList.remove('is-visible');

  const canvas = (
    <div
      ref={wrapRef}
      className="donut-canvas"
      style={{ height, width: legend ? height : undefined, position: 'relative' }}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
    >
      <Doughnut
        data={{
          labels,
          datasets: [{ data: values, backgroundColor: colors || fallback, borderWidth: 0 }],
        }}
        options={options}
      />
      <div ref={tipRef} className="donut-tip" aria-hidden="true" />
    </div>
  );

  if (!legend) {
    return canvas;
  }

  return (
    <div className="donut-with-legend">
      {canvas}
      <ul className="donut-legend">
        {/* Der Platz ist der Schlüssel, nicht die Beschriftung.
            Beschriftungen sind DATEN - hier Kundennamen -, und Daten sind
            nicht eindeutig. Zwei Kunden mit demselben Namen ergaben zwei
            Einträge mit demselben Schlüssel, und React lässt einen davon
            weg: eine Legende, die einen Kunden verschluckt, und ein
            Ringdiagramm, dessen Teile sich nicht mehr zuordnen lassen. Auf
            dem Bildschirm sieht das aus wie ein Kunde weniger.
            Die Reihenfolge ist hier fest (eine sortierte Rangliste, die als
            Ganzes neu kommt), also ist der Platz ein tragfähiger Schlüssel. */}
        {labels.map((label, i) => (
          <li key={i} className="donut-legend-item">
            <span className="donut-legend-dot" style={{ background: colorFor(i) }} aria-hidden="true" />
            <span className="donut-legend-label">{label}</span>
            <span className="donut-legend-value">
              {formatValue ? formatValue(values[i]) : values[i]}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
